import { type Ref, nextTick, watch } from "vue";
import { reportRumError, trackRumAction } from "@/lib/datadog";

interface TeardownStrandGuardOptions {
  /** Selector to count sibling overlays (double-mount / remount-leak detection). */
  countSelector?: string;
  /** Per-surface probes merged into the report context (e.g. backdrop presence). */
  diagnostics?: () => Record<string, unknown>;
  /** Error reporter; defaults to reportRumError (injectable for tests). */
  report?: (message: string, context: Record<string, unknown>) => void;
  /** Persistence-recheck reporter; defaults to trackRumAction (injectable for tests). */
  trackResolved?: (name: string, context: Record<string, unknown>) => void;
}

// PII-safe structural fingerprint of a stranded overlay node — distinguishes the
// competing teardown-failure mechanisms without any customer data. Bounded values
// only: counts, booleans, and the parent tag name; never text content.
// Bounded visibility probe: a stranded panel that occupies space is on screen in
// front of the user, one with no box is an invisible leftover. getBoundingClientRect
// is used rather than offsetParent, which is null for the position:fixed panel.
function isOccupyingSpace(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function domFingerprint(
  el: Element,
  countSelector?: string
): Record<string, unknown> {
  const fingerprint: Record<string, unknown> = {
    parent_tag: el.parentElement?.tagName.toLowerCase() ?? "none",
    teleported_to_body: el.parentElement === document.body,
    // Backdrops currently in the DOM. Named for Flowbite when the drawer was
    // Flowbite-managed; since ENG-311 rebuilt it as a native Vue overlay this
    // attribute is rendered by Seranova/Drawer/Drawer.vue itself, so a rising
    // count means OUR backdrops are accumulating, not that a library is holding
    // the DOM against Vue.
    drawer_backdrops: document.querySelectorAll("[drawer-backdrop]").length,
    // Is the stranded panel actually occupying space, or a zero-size leftover?
    // Replaces a translate-x-0 class probe that the rebuilt drawer never sets
    // (it animates via <Transition name="drawer-slide">), so that probe was
    // false in every report and told us nothing.
    panel_visible: isOccupyingSpace(el)
  };
  if (countSelector) {
    const matches = document.querySelectorAll(countSelector);
    fingerprint.drawer_count = matches.length;
    fingerprint.is_same_node = matches[0] === el;
    // Discriminates the two mechanisms that both show up as a rising
    // drawer_count: one mounted component leaving panels behind (root stays 1)
    // versus the whole drawer being mounted repeatedly (root climbs with it).
    fingerprint.drawer_root_count =
      document.querySelectorAll(".drawer-root").length;
  }
  return fingerprint;
}

// After the initial strand is reported, re-check whether the node ever leaves the
// DOM. Still connected at 1s == a permanent strand (the bug); a node that clears
// shortly after is a slow/animated teardown, not a strand. Separates permanent
// from transient and closes the transition-false-positive question.
function trackPersistence(
  el: Element,
  surface: string,
  track: (name: string, context: Record<string, unknown>) => void
) {
  const horizons: Record<string, boolean> = {};
  setTimeout(() => {
    horizons.connected_250ms = el.isConnected;
  }, 250);
  setTimeout(() => {
    horizons.connected_1000ms = el.isConnected;
    try {
      track("overlay.teardown_resolved", {
        surface,
        view_path: window.location?.pathname,
        connected_250ms: horizons.connected_250ms,
        connected_1000ms: horizons.connected_1000ms,
        ever_removed: !(horizons.connected_250ms && horizons.connected_1000ms)
      });
    } catch {
      // never let instrumentation break the app
    }
  }, 1000);
}

/**
 * Observability (TOF-617). Detects when an overlay (drawer/modal) is asked to
 * close but its element is NOT removed from the DOM after Vue flushes — the
 * teardown did not complete and the teleported overlay is left stranded on
 * screen (the "click-away/close does nothing" symptom).
 *
 * Reports a structured RUM error with a PII-safe DOM fingerprint so the symptom
 * and its mechanism are observable per surface/view/tenant, then re-checks
 * persistence. Observability only — no behavior change.
 *
 * @param isOpen   reactive open-state that gates the overlay's v-if
 * @param getEl    returns the overlay's root element (captured at close time)
 * @param surface  stable label for the overlay, e.g. "schedule-appointment-drawer"
 * @param options  count selector, per-surface diagnostics, and injectable reporters
 */
export function useTeardownStrandGuard(
  isOpen: Ref<boolean>,
  getEl: () => Element | null | undefined,
  surface: string,
  options: TeardownStrandGuardOptions = {}
) {
  const report = options.report ?? reportRumError;
  const trackResolved = options.trackResolved ?? trackRumAction;
  watch(isOpen, (open, wasOpen) => {
    if (!wasOpen || open) return;
    // Capture the element now (still present); after Vue flushes the update it
    // should be gone. If it's still in the DOM, the teardown did not complete.
    const el = getEl();
    if (!el) return;
    nextTick(() => {
      if (!el.isConnected) return; // teardown completed — healthy
      try {
        report("overlay.teardown_stranded", {
          surface,
          view_path: window.location?.pathname,
          ...domFingerprint(el, options.countSelector),
          ...(options.diagnostics?.() ?? {})
        });
        trackPersistence(el, surface, trackResolved);
      } catch {
        // never let instrumentation break the app
      }
    });
  });
}
